import type { WaterGrid } from './WaterGrid';
import type { SandGrid } from '../sandgrid/SandGrid';

export class WaveGenerator {
  private enabled: boolean;
  private baseTideLevel: number;
  private tideAmplitude: number;
  private tideFrequency: number;
  private waveAmplitude: number;
  private waveFrequency: number;

  constructor(
    baseTideLevel: number = 2,
    tideAmplitude: number = 0.2,
    tideFrequency: number = 0.1,
    waveAmplitude: number = 0.8,
    waveFrequency: number = 0.5,
    enabled: boolean = true
  ) {
    this.baseTideLevel = baseTideLevel;
    this.tideAmplitude = tideAmplitude;
    this.tideFrequency = tideFrequency;
    this.waveAmplitude = waveAmplitude;
    this.waveFrequency = waveFrequency;
    this.enabled = enabled;
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public setBaseTideLevel(level: number): void {
    this.baseTideLevel = level;
  }

  public setTideAmplitude(amplitude: number): void {
    this.tideAmplitude = amplitude;
  }

  public setWaveAmplitude(amplitude: number): void {
    this.waveAmplitude = amplitude;
  }

  public generateTide(time: number): number {
    return (
      this.baseTideLevel +
      Math.sin(time * this.tideFrequency) * this.tideAmplitude
    );
  }

  public generateWave(x: number, time: number): number {
    const wave1 = Math.sin(time * this.waveFrequency + x * 0.1);
    const wave2 = 0.5 * Math.sin(time * this.waveFrequency * 1.3 + x * 0.15);
    const wave3 = 0.3 * Math.sin(time * this.waveFrequency * 2.1 + x * 0.08);

    return (this.waveAmplitude * (wave1 + wave2 + wave3)) / 1.8;
  }

  public applyToWaterGrid(
    waterGrid: WaterGrid,
    sandGrid: SandGrid,
    time: number
  ): void {
    if (!this.enabled) return;

    // Tide level represents the absolute water surface height in world space
    const tideLevel = this.generateTide(time);

    // TODO: Move the waves in the other directions.
    // Apply waves from bottom edge only (beach scenario)
    for (let x = 0; x < waterGrid.width; x++) {
      const waveHeight = this.generateWave(x, time);
      const targetAbsoluteHeight = tideLevel + waveHeight;

      const y = waterGrid.height - 1; // Bottom edge
      const sandHeight = sandGrid.get(x, y);

      // Calculate target water depth (water depth above sand)
      // Only have water if target absolute height is above sand
      const targetWaterDepth = Math.max(0, targetAbsoluteHeight - sandHeight);

      const currentWaterDepth = waterGrid.getHeight(x, y);
      const depthDiff = targetWaterDepth - currentWaterDepth;

      // Smoothly move towards target depth
      waterGrid.addHeight(x, y, depthDiff * 0.1);
    }
  }
}
